import React, { useState, useMemo } from 'react';
import { Drawer } from '../ui/Drawer';
import { Button } from '../ui/Button';
import { toast } from 'sonner';
import { usePdfExport } from '../../hooks/usePdfExport';
import type { ProductTemplate } from '../../api/types';

interface Props {
  open: boolean;
  onClose: () => void;
  products: ProductTemplate[];
}

export function ExportPdfDrawer({ open, onClose, products }: Props) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(products.map(p => p.id)));
  const [showCost, setShowCost] = useState(false);
  const [showDescription, setShowDescription] = useState(true);
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  const { isExporting, exportPdf } = usePdfExport();

  const categories = useMemo(() => {
    const cats = new Map<string, ProductTemplate[]>();
    for (const p of products) {
      const list = cats.get(p.category) ?? [];
      list.push(p);
      cats.set(p.category, list);
    }
    return [...cats.entries()];
  }, [products]);

  const toggleAll = () => {
    if (selectedIds.size === products.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(products.map(p => p.id)));
    }
  };

  const toggleCategory = (catProducts: ProductTemplate[]) => {
    const allSelected = catProducts.every(p => selectedIds.has(p.id));
    const next = new Set(selectedIds);
    if (allSelected) {
      for (const p of catProducts) next.delete(p.id);
    } else {
      for (const p of catProducts) next.add(p.id);
    }
    setSelectedIds(next);
  };

  const toggleProduct = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const handleExport = async () => {
    if (selectedIds.size === 0) {
      toast.error('Seleziona almeno un prodotto');
      return;
    }
    const ok = await exportPdf('/api/product-templates/export-pdf', {
      productIds: [...selectedIds],
      showCost,
      showDescription,
      validUntil: validUntil || undefined,
      notes: notes || undefined,
    }, 'listino-prezzi.pdf');
    if (ok) onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Esporta PDF Listino"
      width={480}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button onClick={handleExport} loading={isExporting}>Genera PDF</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        {/* Product selection by category */}
        <div>
          <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <input
              type="checkbox"
              checked={selectedIds.size === products.length}
              ref={el => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < products.length; }}
              onChange={toggleAll}
              style={{ accentColor: 'var(--color-primary)' }}
            />
            Seleziona tutti ({selectedIds.size}/{products.length})
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', maxHeight: 280, overflowY: 'auto' }}>
            {categories.map(([cat, catProducts]) => (
              <div key={cat}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={catProducts.every(p => selectedIds.has(p.id))}
                    ref={el => {
                      if (el) {
                        const allSel = catProducts.every(p => selectedIds.has(p.id));
                        const someSel = catProducts.some(p => selectedIds.has(p.id));
                        el.indeterminate = someSel && !allSel;
                      }
                    }}
                    onChange={() => toggleCategory(catProducts)}
                    style={{ accentColor: 'var(--color-primary)' }}
                  />
                  {cat} ({catProducts.length})
                </label>
                {catProducts.map(p => (
                  <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.25rem 0 0.25rem 1.5rem', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(p.id)}
                      onChange={() => toggleProduct(p.id)}
                      style={{ accentColor: 'var(--color-primary)' }}
                    />
                    {p.name} — &euro;{p.basePrice.toFixed(2)}
                  </label>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Toggles */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
            <input type="checkbox" checked={showCost} onChange={e => setShowCost(e.target.checked)} style={{ accentColor: 'var(--color-primary)' }} />
            Mostra prezzi di costo
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
            <input type="checkbox" checked={showDescription} onChange={e => setShowDescription(e.target.checked)} style={{ accentColor: 'var(--color-primary)' }} />
            Mostra descrizione prodotto
          </label>
        </div>

        {/* Valid until date */}
        <div>
          <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 600, display: 'block', marginBottom: '0.375rem' }}>Valido fino al</label>
          <input
            type="date"
            value={validUntil}
            onChange={e => setValidUntil(e.target.value)}
            style={{
              width: '100%', background: 'var(--glass-bg-card)',
              border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)',
              color: 'var(--color-text)', fontSize: 'var(--text-sm)',
              padding: '0.5rem 0.75rem', outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Notes */}
        <div>
          <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 600, display: 'block', marginBottom: '0.375rem' }}>Note (max 500 caratteri)</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value.slice(0, 500))}
            rows={3}
            placeholder="Note da includere sotto l'intestazione..."
            style={{
              width: '100%', background: 'var(--glass-bg-card)',
              border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)',
              color: 'var(--color-text)', fontSize: 'var(--text-sm)',
              padding: '0.5rem 0.75rem', resize: 'vertical', outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>
    </Drawer>
  );
}
